/**
 * 회사 용어집 — 미리 보기 문장(순수 함수 · 2026-10-23 · 시안 G 2판 ＋ 평 PG2).
 *
 * 서버가 준 계획(`GlossaryPlan`)을 사람이 읽는 줄로만 바꾼다 — 수는 «서버가 센 것»을 그대로 쓴다
 * (콘솔이 따로 세면 규칙이 두 벌이 되고 언젠가 어긋난다).
 *
 * ⚠ 이름 뒤에 조사를 붙이지 않는다 — 「ITSM이」·「큐브리드가」처럼 받침에 따라 갈리는 글자를 틀리지 않게
 *   「밀리는 용어 — 「A」 · 「B」」 꼴로 쓴다.
 */
import type { GlossaryPlace, GlossaryPlan, GlossaryReject } from '../../../api/voiceGlossary';

export type SummaryTone = 'ok' | 'warn' | 'bad' | 'muted';
export interface SummaryLine { tone: SummaryTone; text: string }

const quote = (s: string) => `「${s}」`;

/** 이름 몇 개 — 넘치면 「외 N개」. */
export function nameList(xs: readonly string[], max = 3): string {
  const head = xs.slice(0, max).map(quote).join(' · ');
  return xs.length > max ? `${head} 외 ${xs.length - max}개` : head;
}

export const placeLabel = (p: GlossaryPlace): string => (p === 'top' ? '맨 위' : '맨 아래');

/** 차례 — 「3번」 · 「42~44번」. */
export function positionText(ps: readonly number[]): string {
  if (ps.length === 0) return '';
  return ps.length === 1 ? `${ps[0]}번` : `${ps[0]}~${ps[ps.length - 1]}번`;
}

function rejectText(r: GlossaryReject): string {
  return `${r.line}번째 줄 · ${r.field === 'memo' ? '메모 ' : ''}${r.chars}자`;
}

/** 하나 넣기 미리 보기. */
export function addSummary(plan: GlossaryPlan, enabled: boolean, maxTerms: number): SummaryLine[] {
  if (plan.over_limit) return [{ tone: 'bad', text: `용어는 ${maxTerms}개까지입니다 — 하나를 지운 뒤 넣으세요` }];
  if (plan.added === 0) {
    return plan.skipped_existing.length > 0
      ? [{ tone: 'bad', text: `이미 있는 용어입니다 — ${quote(plan.skipped_existing[0]!)}(대소문자는 가리지 않습니다)` }]
      : [];
  }
  const where = `${placeLabel(plan.place)} ${positionText(plan.new_positions)}에 넣습니다`;
  const lines: SummaryLine[] = [];
  if (!enabled) lines.push({ tone: 'muted', text: `${where} — 꺼 둔 채라 힌트에 들어가지 않습니다` });
  else if (plan.new_in_hint.length > 0) lines.push({ tone: 'ok', text: `${where} — 힌트에 들어갑니다` });
  else lines.push({ tone: 'warn', text: `${where} — 지금은 힌트에 들어가지 않습니다(한도 밖) · 넣은 뒤 위로 올리세요` });
  if (plan.pushed_out.length > 0) {
    lines.push({ tone: 'warn', text: `대신 한도 밖으로 밀리는 용어 — ${nameList(plan.pushed_out)}` });
  }
  return lines;
}

/** 여러 개 넣기 미리 보기. */
export function bulkSummary(plan: GlossaryPlan, maxTerms: number): SummaryLine[] {
  const lines: SummaryLine[] = [];
  if (plan.added > 0) {
    const n = plan.new_in_hint.length;
    const head = `새 용어 ${plan.added}개를 ${placeLabel(plan.place)}(${positionText(plan.new_positions)})에 넣습니다`;
    if (n === plan.added) lines.push({ tone: 'ok', text: `${head} — ${plan.added}개 모두 힌트에 들어갑니다` });
    else if (n > 0) lines.push({ tone: 'ok', text: `${head} — 그중 ${n}개가 힌트에 들어갑니다` });
    else lines.push({ tone: 'warn', text: `${head} — 지금은 힌트에 들어가지 않습니다 · 넣은 뒤 위로 올리세요` });
  } else {
    lines.push({ tone: 'muted', text: '새로 넣을 용어가 없습니다' });
  }
  if (plan.pushed_out.length > 0) {
    lines.push({ tone: 'warn', text: `대신 지금 힌트에 든 ${plan.pushed_out.length}개가 한도 밖으로 밀립니다 — ${nameList(plan.pushed_out)}` });
  }
  if (plan.skipped_existing.length > 0) {
    lines.push({ tone: 'muted', text: `이미 있는 것 ${plan.skipped_existing.length}개 — 건너뜀(${nameList(plan.skipped_existing)})` });
  }
  if (plan.too_long.length > 0) {
    const shown = plan.too_long.slice(0, 3).map(rejectText).join(' · ');
    const more = plan.too_long.length > 3 ? ` 외 ${plan.too_long.length - 3}개` : '';
    lines.push({ tone: 'bad', text: `너무 긴 것 ${plan.too_long.length}개 — 뺌(${shown}${more})` });
  }
  if (plan.over_limit) {
    lines.push({ tone: 'bad', text: `용어는 ${maxTerms}개까지입니다 — 지금 더 넣을 수 있는 것은 ${Math.max(0, plan.added + plan.room)}개` });
  }
  return lines;
}

/** 표의 「넣은 사람」 칸 날짜 — 10-23. */
export function shortDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** 띠의 막대 — 몫을 얼마나 썼나(0~100). */
export function meterPercent(used: number, budget: number): number {
  if (budget <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((used / budget) * 100)));
}
